import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { LogIn, Mail, Lock } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function EmployeeLogin() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await axios.post(`${API}/auth/login`, {
        email,
        password,
      });

      const { token, user } = response.data;

      if (user.role !== "employee") {
        toast.error("Please use admin portal to login");
        setLoading(false);
        return;
      }

      localStorage.setItem("token", token);
      localStorage.setItem("user", JSON.stringify(user));

      toast.success(`Welcome, ${user.name}!`);
      navigate("/employee/dashboard");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="min-h-screen flex items-center justify-center p-4 login-bg"
      style={{
        backgroundImage: "url('https://images.unsplash.com/photo-1707386320792-e158bec69beb?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Nzd8MHwxfHNlYXJjaHw0fHxtb2Rlcm4lMjB0ZWNoJTIwb2ZmaWNlJTIwaW50ZXJpb3IlMjBibHVycmVkJTIwYmFja2dyb3VuZHxlbnwwfHx8fDE3NjQzODA4NzZ8MA&ixlib=rb-4.1.0&q=85')",
      }}
      data-testid="employee-login-page"
    >
      <div className="absolute inset-0 bg-gradient-to-br from-blue-600/90 via-slate-900/95 to-slate-900/95"></div>
      
      <div className="relative w-full max-w-md glass-card rounded-2xl shadow-2xl p-8 fade-in">
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-600 rounded-full mb-4">
            <LogIn className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-3xl font-bold text-slate-900 mb-2">Employee</h1>
          <p className="text-slate-600">
            <a href="http://nix.global/" target="_blank" rel="noopener noreferrer" className="hover:text-blue-600 transition-colors">
              NIX Southeast Asia
            </a>
          </p>
        </div>

        <form onSubmit={handleLogin} className="space-y-5" data-testid="login-form">
          <div className="space-y-2">
            <Label htmlFor="email" className="text-slate-700 font-medium">
              Email
            </Label>
            <div className="relative">
              <Mail className="absolute left-3 top-3 h-5 w-5 text-slate-400" />
              <Input
                id="email"
                type="email"
                placeholder="name@nix-southeast-asia.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="pl-10 h-12 bg-white border-slate-200 focus:border-blue-600 focus:ring-2 focus:ring-blue-600/20"
                data-testid="email-input"
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="password" className="text-slate-700 font-medium">
              Password
            </Label>
            <div className="relative">
              <Lock className="absolute left-3 top-3 h-5 w-5 text-slate-400" />
              <Input
                id="password"
                type="password"
                placeholder="Enter your password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="pl-10 h-12 bg-white border-slate-200 focus:border-blue-600 focus:ring-2 focus:ring-blue-600/20"
                data-testid="password-input"
              />
            </div>
          </div>

          <Button
            type="submit"
            disabled={loading}
            className="w-full h-12 bg-blue-600 hover:bg-blue-700 text-white font-medium shadow-lg shadow-blue-600/20 transition-all active:scale-95"
            data-testid="login-submit-button"
          >
            {loading ? "Loading..." : "Login"}
          </Button>
        </form>

        <div className="mt-6 text-center">
          <Link
            to="/login/admin"
            className="text-sm text-blue-600 hover:text-blue-700 font-medium"
            data-testid="admin-login-link"
          >
            Login as Admin →
          </Link>
        </div>
      </div>
    </div>
  );
}